
import { ParallaxRegistry } from './ParallaxRegistry';
import { ParallaxEffectType } from '../../types';
import { StarryNightEffect } from './effects/StarryNightEffect';
import { GridWorldEffect } from './effects/GridWorldEffect';
import { NebulaDriftEffect } from './effects/NebulaDriftEffect';
import { FloatingShapesEffect } from './effects/FloatingShapesEffect';
import { DataStreamEffect } from './effects/DataStreamEffect';
import { WatermarkEffect } from './effects/WatermarkEffect';
import { GeometricAbstractionEffect } from './effects/GeometricAbstractionEffect';
import { VortexTunnelEffect } from './effects/VortexTunnelEffect';
import { ParticleFieldEffect } from './effects/ParticleFieldEffect';
import { AuroraWavesEffect } from './effects/AuroraWavesEffect';
import { CircuitBoardEffect } from './effects/CircuitBoardEffect';
import { RetroGridEffect } from './effects/RetroGridEffect';
import { MatrixRainEffect } from './effects/MatrixRainEffect';
import { FractalCloudsEffect } from './effects/FractalCloudsEffect';
import { CyberpunkCityEffect } from './effects/CyberpunkCityEffect';
import { OceanWavesEffect } from './effects/OceanWavesEffect';
import { DNAHelixEffect } from './effects/DNAHelixEffect';
import { ConstellationsEffect } from './effects/ConstellationsEffect';
import { HexagonGridEffect } from './effects/HexagonGridEffect';
import { SpiralGalaxyEffect } from './effects/SpiralGalaxyEffect';

// Phase 2: Nature & Organic Imports
import { LavaFlowEffect } from './effects/LavaFlowEffect';
import { MossGrowthEffect } from './effects/MossGrowthEffect';
import { CloudFormationsEffect } from './effects/CloudFormationsEffect';
import { RootSystemsEffect } from './effects/RootSystemsEffect';
import { RipplePoolsEffect } from './effects/RipplePoolsEffect';
import { RainStreaksEffect } from './effects/RainStreaksEffect';
import { FallingLeavesEffect } from './effects/FallingLeavesEffect';
import { SnowFlurryEffect } from './effects/SnowFlurryEffect';
import { SwayingGrassEffect } from './effects/SwayingGrassEffect';
import { SandDunesEffect } from './effects/SandDunesEffect';
import { CoralReefEffect } from './effects/CoralReefEffect';
import { BambooForestEffect } from './effects/BambooForestEffect';
import { ThunderStormEffect } from './effects/ThunderStormEffect';
import { FireEmbersEffect } from './effects/FireEmbersEffect';
import { JungleVinesEffect } from './effects/JungleVinesEffect';
import { CherryBlossomEffect } from './effects/CherryBlossomEffect';
import { IceCrystalsEffect } from './effects/IceCrystalsEffect';
import { RiverCurrentEffect } from './effects/RiverCurrentEffect';
import { VolcanicAshEffect } from './effects/VolcanicAshEffect';
import { MistDriftEffect } from './effects/MistDriftEffect';
import { BioLuminescenceEffect } from './effects/BioLuminescenceEffect';
import { PetalStormEffect } from './effects/PetalStormEffect';
import { SunBeamsEffect } from './effects/SunBeamsEffect';
import { UnderwaterLightEffect } from './effects/UnderwaterLightEffect';
import { SporeDriftEffect } from './effects/SporeDriftEffect';
import { WindWhirlsEffect } from './effects/WindWhirlsEffect';

// Phase 3: Abstract & Geometric Imports
import { VoronoiCellsEffect } from './effects/VoronoiCellsEffect';
import { MoirePatternsEffect } from './effects/MoirePatternsEffect';
import { LissajousCurvesEffect } from './effects/LissajousCurvesEffect';
import { FlowerOfLifeEffect } from './effects/FlowerOfLifeEffect';
import { KaleidoscopeMirrorEffect } from './effects/KaleidoscopeMirrorEffect';
import { FractalMandalasEffect } from './effects/FractalMandalasEffect';
import { SacredGeometryEffect } from './effects/SacredGeometryEffect';
import { MetatronCubeEffect } from './effects/MetatronCubeEffect';
import { MandelbrotSetEffect } from './effects/MandelbrotSetEffect';
import { JuliaSetEffect } from './effects/JuliaSetEffect';
import { PenroseTilingEffect } from './effects/PenroseTilingEffect';
import { SierpinskiTriangleEffect } from './effects/SierpinskiTriangleEffect';
import { KochSnowflakeEffect } from './effects/KochSnowflakeEffect';
import { HarmonicWavesEffect } from './effects/HarmonicWavesEffect';
import { IsometricCubesEffect } from './effects/IsometricCubesEffect';
import { DotMatrixEffect } from './effects/DotMatrixEffect';
import { WireframeTerrainEffect } from './effects/WireframeTerrainEffect';
import { PolygonSpiralsEffect } from './effects/PolygonSpiralsEffect';
import { TessellatedHexagonsEffect } from './effects/TessellatedHexagonsEffect';
import { MinimalistLinesEffect } from './effects/MinimalistLinesEffect';
import { BrutalistBlocksEffect } from './effects/BrutalistBlocksEffect';
import { OpArtIllusionEffect } from './effects/OpArtIllusionEffect';
import { SymmetricOrbsEffect } from './effects/SymmetricOrbsEffect';
import { HypnoticSpiralsEffect } from './effects/HypnoticSpiralsEffect';
import { TriangularSubdivisionEffect } from './effects/TriangularSubdivisionEffect';
import { CrystallineLatticeEffect } from './effects/CrystallineLatticeEffect';
import { FluidSubstanceEffect } from './effects/FluidSubstanceEffect';
import { VectorFieldEffect } from './effects/VectorFieldEffect';

// Phase 4: Cyber & Tactical Imports
import { HUDOverlayEffect } from './effects/HUDOverlayEffect';
import { TerminalStreamsEffect } from './effects/TerminalStreamsEffect';
import { DataNodesEffect } from './effects/DataNodesEffect';
import { RadarSweepEffect } from './effects/RadarSweepEffect';
import { SchematicGridEffect } from './effects/SchematicGridEffect';
import { GlitchPixelsEffect } from './effects/GlitchPixelsEffect';
import { ScanlineProcessorEffect } from './effects/ScanlineProcessorEffect';
import { BinaryCascadeEffect } from './effects/BinaryCascadeEffect';
import { CircuitFlowEffect } from './effects/CircuitFlowEffect';
import { NeuralNetworkEffect } from './effects/NeuralNetworkEffect';
import { FirmwareDiagramEffect } from './effects/FirmwareDiagramEffect';
import { TargetingReticlesEffect } from './effects/TargetingReticlesEffect';
import { SatelliteLinkEffect } from './effects/SatelliteLinkEffect';
import { SecurityMeshEffect } from './effects/SecurityMeshEffect';
import { EncryptionMatrixEffect } from './effects/EncryptionMatrixEffect';
import { TechSpecsEffect } from './effects/TechSpecsEffect';
import { AnalogNoiseEffect } from './effects/AnalogNoiseEffect';
import { VirtualRealityEffect } from './effects/VirtualRealityEffect';
import { CyberSpaceEffect } from './effects/CyberSpaceEffect';
import { DigitalSignalEffect } from './effects/DigitalSignalEffect';
import { PulseWaveEffect } from './effects/PulseWaveEffect';
import { SignalInterferenceEffect } from './effects/SignalInterferenceEffect';
import { BioHazardEffect } from './effects/BioHazardEffect';
import { RadiationScanEffect } from './effects/RadiationScanEffect';
import { SonarPingEffect } from './effects/SonarPingEffect';
import { CoordinateSystemEffect } from './effects/CoordinateSystemEffect';
import { DebugConsoleEffect } from './effects/DebugConsoleEffect';
import { SystemMainframeEffect } from './effects/SystemMainframeEffect';

// Phase 5: Cosmic & Dimensional Imports
import { SupernovaEffect } from './effects/SupernovaEffect';
import { EventHorizonEffect } from './effects/EventHorizonEffect';
import { DarkMatterEffect } from './effects/DarkMatterEffect';
import { StarClustersEffect } from './effects/StarClustersEffect';
import { WormholeEffect } from './effects/WormholeEffect';
import { CosmicBackgroundEffect } from './effects/CosmicBackgroundEffect';
import { QuantumFoamEffect } from './effects/QuantumFoamEffect';
import { SolarFlareEffect } from './effects/SolarFlareEffect';
import { PulsarBeamEffect } from './effects/PulsarBeamEffect';
import { NebulaGlowEffect } from './effects/NebulaGlowEffect';
import { BlackHoleSingularityEffect } from './effects/BlackHoleSingularityEffect';
import { QuasarJetEffect } from './effects/QuasarJetEffect';
import { GalacticCoreEffect } from './effects/GalacticCoreEffect';
import { StellarNurseryEffect } from './effects/StellarNurseryEffect';
import { CometTailEffect } from './effects/CometTailEffect';
import { MeteorShowerEffect } from './effects/MeteorShowerEffect';
import { AtmosphericEntryEffect } from './effects/AtmosphericEntryEffect';
import { DimensionalRiftEffect } from './effects/DimensionalRiftEffect';
import { StringsTheoryEffect } from './effects/StringsTheoryEffect';
import { MultiverseBubblesEffect } from './effects/MultiverseBubblesEffect';
import { EntropyHeatDeathEffect } from './effects/EntropyHeatDeathEffect';
import { GravityWavesEffect } from './effects/GravityWavesEffect';
import { SpaceTimeDistortionEffect } from './effects/SpaceTimeDistortionEffect';
import { AuroraBorealisEffect } from './effects/AuroraBorealisEffect';
import { AntiMatterEffect } from './effects/AntiMatterEffect';
import { VoidVoidEffect } from './effects/VoidVoidEffect';
import { CelestialBodyEffect } from './effects/CelestialBodyEffect';
import { ExpandingUniverseEffect } from './effects/ExpandingUniverseEffect';

// Phase 6: Artistic & Texture Imports
import { OilPaintingEffect } from './effects/OilPaintingEffect';
import { SketchBookEffect } from './effects/SketchBookEffect';
import { StainedGlassEffect } from './effects/StainedGlassEffect';
import { MosaicTilesEffect } from './effects/MosaicTilesEffect';
import { CarbonFiberEffect } from './effects/CarbonFiberEffect';
import { BrushedMetalEffect } from './effects/BrushedMetalEffect';
import { LiquidMetalEffect } from './effects/LiquidMetalEffect';
import { InkBleedEffect } from './effects/InkBleedEffect';
import { WatercolorEffect } from './effects/WatercolorEffect';
import { PopArtEffect } from './effects/PopArtEffect';
import { ComicDotsEffect } from './effects/ComicDotsEffect';
import { OldFilmEffect } from './effects/OldFilmEffect';
import { VHSGlitchEffect } from './effects/VHSGlitchEffect';
import { PrismLightEffect } from './effects/PrismLightEffect';
import { ChalkDrawingEffect } from './effects/ChalkDrawingEffect';
import { PixelArtLandscapeEffect } from './effects/PixelArtLandscapeEffect';
import { SynthwaveGridEffect } from './effects/SynthwaveGridEffect';
import { VaporWaveEffect } from './effects/VaporWaveEffect';
import { CharcoalSketchEffect } from './effects/CharcoalSketchEffect';
import { CrossHatchEffect } from './effects/CrossHatchEffect';
import { WoodGrainEffect } from './effects/WoodGrainEffect';
import { MarbleTextureEffect } from './effects/MarbleTextureEffect';

// Phase 7: Media & External Imports
import { ImageOverlayEffect } from './effects/ImageOverlayEffect';
import { ScrollingImageEffect } from './effects/ScrollingImageEffect';
import { VideoBackdropEffect } from './effects/VideoBackdropEffect';
import { VideoTextureEffect } from './effects/VideoTextureEffect';
import { MediaMosaicEffect } from './effects/MediaMosaicEffect';

export const initializeParallaxRegistry = () => {
    ParallaxRegistry.register(ParallaxEffectType.STARRY_NIGHT, new StarryNightEffect());
    ParallaxRegistry.register(ParallaxEffectType.GRID_WORLD, new GridWorldEffect());
    ParallaxRegistry.register(ParallaxEffectType.NEBULA_DRIFT, new NebulaDriftEffect());
    ParallaxRegistry.register(ParallaxEffectType.FLOATING_SHAPES, new FloatingShapesEffect());
    ParallaxRegistry.register(ParallaxEffectType.DATA_STREAM, new DataStreamEffect());
    ParallaxRegistry.register(ParallaxEffectType.WATERMARK, new WatermarkEffect());
    ParallaxRegistry.register(ParallaxEffectType.GEOMETRIC_ABSTRACTION, new GeometricAbstractionEffect());
    ParallaxRegistry.register(ParallaxEffectType.VORTEX_TUNNEL, new VortexTunnelEffect());
    ParallaxRegistry.register(ParallaxEffectType.PARTICLE_FIELD, new ParticleFieldEffect());
    ParallaxRegistry.register(ParallaxEffectType.AURORA_WAVES, new AuroraWavesEffect());
    ParallaxRegistry.register(ParallaxEffectType.CIRCUIT_BOARD, new CircuitBoardEffect());
    ParallaxRegistry.register(ParallaxEffectType.RETRO_GRID, new RetroGridEffect());
    ParallaxRegistry.register(ParallaxEffectType.MATRIX_RAIN, new MatrixRainEffect());
    ParallaxRegistry.register(ParallaxEffectType.FRACTAL_CLOUDS, new FractalCloudsEffect());
    ParallaxRegistry.register(ParallaxEffectType.CYBER_PUNK_CITY, new CyberpunkCityEffect());
    ParallaxRegistry.register(ParallaxEffectType.OCEAN_WAVES, new OceanWavesEffect());
    ParallaxRegistry.register(ParallaxEffectType.DNA_HELIX, new DNAHelixEffect());
    ParallaxRegistry.register(ParallaxEffectType.CONSTELLATIONS, new ConstellationsEffect());
    ParallaxRegistry.register(ParallaxEffectType.HEXAGON_GRID, new HexagonGridEffect());
    ParallaxRegistry.register(ParallaxEffectType.SPIRAL_GALAXY, new SpiralGalaxyEffect());
    
    // Phase 2: Nature & Organic Registration
    ParallaxRegistry.register(ParallaxEffectType.LAVA_FLOW, new LavaFlowEffect());
    ParallaxRegistry.register(ParallaxEffectType.MOSS_GROWTH, new MossGrowthEffect());
    ParallaxRegistry.register(ParallaxEffectType.CLOUD_FORMATIONS, new CloudFormationsEffect());
    ParallaxRegistry.register(ParallaxEffectType.ROOT_SYSTEMS, new RootSystemsEffect());
    ParallaxRegistry.register(ParallaxEffectType.RIPPLE_POOLS, new RipplePoolsEffect());
    ParallaxRegistry.register(ParallaxEffectType.RAIN_STREAKS, new RainStreaksEffect());
    ParallaxRegistry.register(ParallaxEffectType.FALLING_LEAVES, new FallingLeavesEffect());
    ParallaxRegistry.register(ParallaxEffectType.SNOW_FLURRY, new SnowFlurryEffect());
    ParallaxRegistry.register(ParallaxEffectType.SWAYING_GRASS, new SwayingGrassEffect());
    ParallaxRegistry.register(ParallaxEffectType.SAND_DUNES, new SandDunesEffect());
    ParallaxRegistry.register(ParallaxEffectType.CORAL_REEF, new CoralReefEffect());
    ParallaxRegistry.register(ParallaxEffectType.BAMBOO_FOREST, new BambooForestEffect());
    ParallaxRegistry.register(ParallaxEffectType.THUNDER_STORM, new ThunderStormEffect());
    ParallaxRegistry.register(ParallaxEffectType.FIRE_EMBERS, new FireEmbersEffect());
    ParallaxRegistry.register(ParallaxEffectType.JUNGLE_VINES, new JungleVinesEffect());
    ParallaxRegistry.register(ParallaxEffectType.CHERRY_BLOSSOM, new CherryBlossomEffect());
    ParallaxRegistry.register(ParallaxEffectType.ICE_CRYSTALS, new IceCrystalsEffect());
    ParallaxRegistry.register(ParallaxEffectType.RIVER_CURRENT, new RiverCurrentEffect());
    ParallaxRegistry.register(ParallaxEffectType.VOLCANIC_ASH, new VolcanicAshEffect());
    ParallaxRegistry.register(ParallaxEffectType.MIST_DRIFT, new MistDriftEffect());
    ParallaxRegistry.register(ParallaxEffectType.BIO_LUMINESCENCE, new BioLuminescenceEffect());
    ParallaxRegistry.register(ParallaxEffectType.PETAL_STORM, new PetalStormEffect());
    ParallaxRegistry.register(ParallaxEffectType.SUN_BEAMS, new SunBeamsEffect());
    ParallaxRegistry.register(ParallaxEffectType.UNDERWATER_LIGHT, new UnderwaterLightEffect());
    ParallaxRegistry.register(ParallaxEffectType.SPORE_DRIFT, new SporeDriftEffect());
    ParallaxRegistry.register(ParallaxEffectType.WIND_WHIRLS, new WindWhirlsEffect());

    // Phase 3: Abstract & Geometric Registration
    ParallaxRegistry.register(ParallaxEffectType.VORONOI_CELLS, new VoronoiCellsEffect());
    ParallaxRegistry.register(ParallaxEffectType.MOIRE_PATTERNS, new MoirePatternsEffect());
    ParallaxRegistry.register(ParallaxEffectType.LISSAJOUS_CURVES, new LissajousCurvesEffect());
    ParallaxRegistry.register(ParallaxEffectType.FLOWER_OF_LIFE, new FlowerOfLifeEffect());
    ParallaxRegistry.register(ParallaxEffectType.KALEIDOSCOPE_MIRROR, new KaleidoscopeMirrorEffect());
    ParallaxRegistry.register(ParallaxEffectType.FRACTAL_MANDALAS, new FractalMandalasEffect());
    ParallaxRegistry.register(ParallaxEffectType.SACRED_GEOMETRY, new SacredGeometryEffect());
    ParallaxRegistry.register(ParallaxEffectType.METATRON_CUBE, new MetatronCubeEffect());
    ParallaxRegistry.register(ParallaxEffectType.MANDELBROT_SET, new MandelbrotSetEffect());
    ParallaxRegistry.register(ParallaxEffectType.JULIA_SET, new JuliaSetEffect());
    ParallaxRegistry.register(ParallaxEffectType.PENROSE_TILING, new PenroseTilingEffect());
    ParallaxRegistry.register(ParallaxEffectType.SIERPINSKI_TRIANGLE, new SierpinskiTriangleEffect());
    ParallaxRegistry.register(ParallaxEffectType.KOCH_SNOWFLAKE, new KochSnowflakeEffect());
    ParallaxRegistry.register(ParallaxEffectType.HARMONIC_WAVES, new HarmonicWavesEffect());
    ParallaxRegistry.register(ParallaxEffectType.ISOMETRIC_CUBES, new IsometricCubesEffect());
    ParallaxRegistry.register(ParallaxEffectType.DOT_MATRIX, new DotMatrixEffect());
    ParallaxRegistry.register(ParallaxEffectType.WIREFRAME_TERRAIN, new WireframeTerrainEffect());
    ParallaxRegistry.register(ParallaxEffectType.POLYGON_SPIRALS, new PolygonSpiralsEffect());
    ParallaxRegistry.register(ParallaxEffectType.TESSELLATED_HEXAGONS, new TessellatedHexagonsEffect());
    ParallaxRegistry.register(ParallaxEffectType.MINIMALIST_LINES, new MinimalistLinesEffect());
    ParallaxRegistry.register(ParallaxEffectType.BRUTALIST_BLOCKS, new BrutalistBlocksEffect());
    ParallaxRegistry.register(ParallaxEffectType.OP_ART_ILLUSION, new OpArtIllusionEffect());
    ParallaxRegistry.register(ParallaxEffectType.SYMMETRIC_ORBS, new SymmetricOrbsEffect());
    ParallaxRegistry.register(ParallaxEffectType.HYPNOTIC_SPIRALS, new HypnoticSpiralsEffect());
    ParallaxRegistry.register(ParallaxEffectType.TRIANGULAR_SUBDIVISION, new TriangularSubdivisionEffect());
    ParallaxRegistry.register(ParallaxEffectType.CRYSTALLINE_LATTICE, new CrystallineLatticeEffect());
    ParallaxRegistry.register(ParallaxEffectType.FLUID_SUBSTANCE, new FluidSubstanceEffect());
    ParallaxRegistry.register(ParallaxEffectType.VECTOR_FIELD, new VectorFieldEffect());

    // Phase 4: Cyber & Tactical Registration
    ParallaxRegistry.register(ParallaxEffectType.HUD_OVERLAY, new HUDOverlayEffect());
    ParallaxRegistry.register(ParallaxEffectType.TERMINAL_STREAMS, new TerminalStreamsEffect());
    ParallaxRegistry.register(ParallaxEffectType.DATA_NODES, new DataNodesEffect());
    ParallaxRegistry.register(ParallaxEffectType.RADAR_SWEEP, new RadarSweepEffect());
    ParallaxRegistry.register(ParallaxEffectType.SCHEMATIC_GRID, new SchematicGridEffect());
    ParallaxRegistry.register(ParallaxEffectType.GLITCH_PIXELS, new GlitchPixelsEffect());
    ParallaxRegistry.register(ParallaxEffectType.SCANLINE_PROCESSOR, new ScanlineProcessorEffect());
    ParallaxRegistry.register(ParallaxEffectType.BINARY_CASCADE, new BinaryCascadeEffect());
    ParallaxRegistry.register(ParallaxEffectType.CIRCUIT_FLOW, new CircuitFlowEffect());
    ParallaxRegistry.register(ParallaxEffectType.NEURAL_NETWORK, new NeuralNetworkEffect());
    ParallaxRegistry.register(ParallaxEffectType.FIRMWARE_DIAGRAM, new FirmwareDiagramEffect());
    ParallaxRegistry.register(ParallaxEffectType.TARGETING_RETICLES, new TargetingReticlesEffect());
    ParallaxRegistry.register(ParallaxEffectType.SATELLITE_LINK, new SatelliteLinkEffect());
    ParallaxRegistry.register(ParallaxEffectType.SECURITY_MESH, new SecurityMeshEffect());
    ParallaxRegistry.register(ParallaxEffectType.ENCRYPTION_MATRIX, new EncryptionMatrixEffect());
    ParallaxRegistry.register(ParallaxEffectType.TECH_SPECS, new TechSpecsEffect());
    ParallaxRegistry.register(ParallaxEffectType.ANALOG_NOISE, new AnalogNoiseEffect());
    ParallaxRegistry.register(ParallaxEffectType.VIRTUAL_REALITY, new VirtualRealityEffect());
    ParallaxRegistry.register(ParallaxEffectType.CYBER_SPACE, new CyberSpaceEffect());
    ParallaxRegistry.register(ParallaxEffectType.DIGITAL_SIGNAL, new DigitalSignalEffect());
    ParallaxRegistry.register(ParallaxEffectType.PULSE_WAVE, new PulseWaveEffect());
    ParallaxRegistry.register(ParallaxEffectType.SIGNAL_INTERFERENCE, new SignalInterferenceEffect());
    ParallaxRegistry.register(ParallaxEffectType.BIO_HAZARD, new BioHazardEffect());
    ParallaxRegistry.register(ParallaxEffectType.RADIATION_SCAN, new RadiationScanEffect());
    ParallaxRegistry.register(ParallaxEffectType.SONAR_PING, new SonarPingEffect());
    ParallaxRegistry.register(ParallaxEffectType.COORDINATE_SYSTEM, new CoordinateSystemEffect());
    ParallaxRegistry.register(ParallaxEffectType.DEBUG_CONSOLE, new DebugConsoleEffect());
    ParallaxRegistry.register(ParallaxEffectType.SYSTEM_MAINFRAME, new SystemMainframeEffect());

    // Phase 5: Cosmic & Dimensional Registration
    ParallaxRegistry.register(ParallaxEffectType.SUPERNOVA, new SupernovaEffect());
    ParallaxRegistry.register(ParallaxEffectType.EVENT_HORIZON, new EventHorizonEffect());
    ParallaxRegistry.register(ParallaxEffectType.DARK_MATTER, new DarkMatterEffect());
    ParallaxRegistry.register(ParallaxEffectType.STAR_CLUSTERS, new StarClustersEffect());
    ParallaxRegistry.register(ParallaxEffectType.WORMHOLE, new WormholeEffect());
    ParallaxRegistry.register(ParallaxEffectType.COSMIC_BACKGROUND, new CosmicBackgroundEffect());
    ParallaxRegistry.register(ParallaxEffectType.QUANTUM_FOAM, new QuantumFoamEffect());
    ParallaxRegistry.register(ParallaxEffectType.SOLAR_FLARE, new SolarFlareEffect());
    ParallaxRegistry.register(ParallaxEffectType.PULSAR_BEAM, new PulsarBeamEffect());
    ParallaxRegistry.register(ParallaxEffectType.NEBULA_GLOW, new NebulaGlowEffect());
    ParallaxRegistry.register(ParallaxEffectType.BLACK_HOLE_SINGULARITY, new BlackHoleSingularityEffect());
    ParallaxRegistry.register(ParallaxEffectType.QUASAR_JET, new QuasarJetEffect());
    ParallaxRegistry.register(ParallaxEffectType.GALACTIC_CORE, new GalacticCoreEffect());
    ParallaxRegistry.register(ParallaxEffectType.STELLAR_NURSERY, new StellarNurseryEffect());
    ParallaxRegistry.register(ParallaxEffectType.COMET_TAIL, new CometTailEffect());
    ParallaxRegistry.register(ParallaxEffectType.METEOR_SHOWER, new MeteorShowerEffect());
    ParallaxRegistry.register(ParallaxEffectType.ATMOSPHERIC_ENTRY, new AtmosphericEntryEffect());
    ParallaxRegistry.register(ParallaxEffectType.DIMENSIONAL_RIFT, new DimensionalRiftEffect());
    ParallaxRegistry.register(ParallaxEffectType.STRINGS_THEORY, new StringsTheoryEffect());
    ParallaxRegistry.register(ParallaxEffectType.MULTIVERSE_BUBBLES, new MultiverseBubblesEffect());
    ParallaxRegistry.register(ParallaxEffectType.ENTROPY_HEAT_DEATH, new EntropyHeatDeathEffect());
    ParallaxRegistry.register(ParallaxEffectType.GRAVITY_WAVES, new GravityWavesEffect());
    ParallaxRegistry.register(ParallaxEffectType.SPACE_TIME_DISTORTION, new SpaceTimeDistortionEffect());
    ParallaxRegistry.register(ParallaxEffectType.AURORA_BOREALIS, new AuroraBorealisEffect());
    ParallaxRegistry.register(ParallaxEffectType.ANTI_MATTER, new AntiMatterEffect());
    ParallaxRegistry.register(ParallaxEffectType.VOID_VOID, new VoidVoidEffect());
    ParallaxRegistry.register(ParallaxEffectType.CELESTIAL_BODY, new CelestialBodyEffect());
    ParallaxRegistry.register(ParallaxEffectType.EXPANDING_UNIVERSE, new ExpandingUniverseEffect());

    // Phase 6: Artistic & Texture Registration
    ParallaxRegistry.register(ParallaxEffectType.OIL_PAINTING, new OilPaintingEffect());
    ParallaxRegistry.register(ParallaxEffectType.SKETCH_BOOK, new SketchBookEffect());
    ParallaxRegistry.register(ParallaxEffectType.STAINED_GLASS, new StainedGlassEffect());
    ParallaxRegistry.register(ParallaxEffectType.MOSAIC_TILES, new MosaicTilesEffect());
    ParallaxRegistry.register(ParallaxEffectType.CARBON_FIBER, new CarbonFiberEffect());
    ParallaxRegistry.register(ParallaxEffectType.BRUSHED_METAL, new BrushedMetalEffect());
    ParallaxRegistry.register(ParallaxEffectType.LIQUID_METAL, new LiquidMetalEffect());
    ParallaxRegistry.register(ParallaxEffectType.INK_BLEED, new InkBleedEffect());
    ParallaxRegistry.register(ParallaxEffectType.WATERCOLOR, new WatercolorEffect());
    ParallaxRegistry.register(ParallaxEffectType.POP_ART, new PopArtEffect());
    ParallaxRegistry.register(ParallaxEffectType.COMIC_DOTS, new ComicDotsEffect());
    ParallaxRegistry.register(ParallaxEffectType.OLD_FILM, new OldFilmEffect());
    ParallaxRegistry.register(ParallaxEffectType.VHS_GLITCH, new VHSGlitchEffect());
    ParallaxRegistry.register(ParallaxEffectType.PRISM_LIGHT, new PrismLightEffect());
    ParallaxRegistry.register(ParallaxEffectType.CHALK_DRAWING, new ChalkDrawingEffect());
    ParallaxRegistry.register(ParallaxEffectType.PIXEL_ART_LANDSCAPE, new PixelArtLandscapeEffect());
    ParallaxRegistry.register(ParallaxEffectType.SYNTHWAVE_GRID, new SynthwaveGridEffect());
    ParallaxRegistry.register(ParallaxEffectType.VAPOR_WAVE, new VaporWaveEffect());
    ParallaxRegistry.register(ParallaxEffectType.CHARCOAL_SKETCH, new CharcoalSketchEffect());
    ParallaxRegistry.register(ParallaxEffectType.CROSS_HATCH, new CrossHatchEffect());
    ParallaxRegistry.register(ParallaxEffectType.WOOD_GRAIN, new WoodGrainEffect());
    ParallaxRegistry.register(ParallaxEffectType.MARBLE_TEXTURE, new MarbleTextureEffect());

    // Phase 7: Media & External Registration
    ParallaxRegistry.register(ParallaxEffectType.IMAGE_OVERLAY, new ImageOverlayEffect());
    ParallaxRegistry.register(ParallaxEffectType.SCROLLING_IMAGE, new ScrollingImageEffect());
    ParallaxRegistry.register(ParallaxEffectType.VIDEO_BACKDROP, new VideoBackdropEffect());
    ParallaxRegistry.register(ParallaxEffectType.VIDEO_TEXTURE, new VideoTextureEffect());
    ParallaxRegistry.register(ParallaxEffectType.MEDIA_MOSAIC, new MediaMosaicEffect());

    // Digital Rain and Tech Pixels can be added or alias existing ones
    ParallaxRegistry.register(ParallaxEffectType.DIGITAL_RAIN, new TerminalStreamsEffect());
    ParallaxRegistry.register(ParallaxEffectType.NEON_LINES, new CircuitFlowEffect());
    ParallaxRegistry.register(ParallaxEffectType.BUBBLE_FIELD, new ParticleFieldEffect()); 
    ParallaxRegistry.register(ParallaxEffectType.ABSTRACT_WAVES, new AuroraWavesEffect()); 
    ParallaxRegistry.register(ParallaxEffectType.TECH_PIXELS, new GlitchPixelsEffect());
};
